import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './Button';
import styles from './DisclaimerGate.module.css';

// Bump the version whenever the notice's substance changes, so everyone sees it again.
const ACK_KEY = 'whaletail.disclaimerAcknowledged.v3';

function hasAcknowledged(): boolean {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(ACK_KEY) === '1';
  } catch {
    return false;
  }
}

export function DisclaimerGate() {
  const [open, setOpen] = useState(false);
  const headingId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!hasAcknowledged()) {
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    buttonRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  const acknowledge = () => {
    try {
      window.localStorage.setItem(ACK_KEY, '1');
    } catch {
      // Storage may be unavailable; the gate will simply reappear next visit.
    }
    setOpen(false);
  };

  return (
    <div className={styles.overlay} role="presentation">
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
      >
        <h2 id={headingId} className={styles.heading}>
          Not financial or betting advice
        </h2>
        <p>
          WhaleTail is a read-only research tool. Nothing here is financial, investment,
          trading, betting, tax, or legal advice, or a recommendation to buy, sell, hold,
          trade, or bet.
        </p>
        <p>
          Signals, model odds, and simulated results are experiments built on third-party
          data that can be delayed or wrong, and most have shown no reliable edge.
          Prediction markets and sports betting carry real risk of loss and may be
          restricted where you live. Everything is provided as is, and you are solely
          responsible for your decisions.
        </p>
        <p className={styles.fine}>
          By continuing, you agree to WhaleTail&apos;s{' '}
          <Link to="/terms" onClick={acknowledge}>
            Terms of Use
          </Link>{' '}
          and{' '}
          <Link to="/privacy" onClick={acknowledge}>
            Privacy Policy
          </Link>
          .
        </p>
        <div className={styles.actions}>
          <Button
            ref={buttonRef}
            variant="primary"
            className={styles.button}
            onClick={acknowledge}
          >
            I understand this isn't advice
          </Button>
        </div>
      </div>
    </div>
  );
}
